/**
 * AuthGuard component that protects the entire app
 * Shows login screen when user is not authenticated
 * Shows unauthorized screen when user is not in allowed users table
 */

import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Login from '../Login/Login';
import styles from './AuthGuard.module.css';

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { isAuthenticated, isLoading, isRoleLoading, isUnauthorized, user, signOut } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Loading...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  // Show loading while checking authorization
  if (isRoleLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Verifying access...</p>
        </div>
      </div>
    );
  }

  // Show unauthorized screen if user is not in allowed users table
  if (isUnauthorized) {
    return (
      <div className={styles.unauthorizedContainer}>
        <div className={styles.unauthorizedCard}>
          <div className={styles.unauthorizedIcon}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 className={styles.unauthorizedTitle}>Access Denied</h1>
          <p className={styles.unauthorizedMessage}>
            Your account <strong>{user?.email}</strong> is not authorized to access this application.
          </p>
          <p className={styles.unauthorizedHint}>
            Please contact an administrator to request access.
          </p>
          <button
            className={styles.signOutButton}
            onClick={() => signOut()}
          >
            Sign out and try different account
          </button>
        </div>
      </div>
    );
  }

  // Show the app if authenticated and authorized
  return <>{children}</>;
};

export default AuthGuard;